import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Clock,
  Gift,
  Layers,
  Lightbulb,
  LogOut,
  PiggyBank,
  Radio,
  Repeat,
  Ruler,
  Scale,
  Search,
  ShieldOff,
  Target,
  TrendingUp,
  Trophy,
  Umbrella,
  Wallet,
  RotateCcw,
} from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { CATEGORIES, LESSONS, lessonsByCategory } from "@/data/playbook";
import { EVENTS, track } from "@/lib/analytics";
import { getCompletedSlugs, resetProgress } from "@/lib/playbookProgress";
import { PLAYBOOK } from "@/constants/testIds";

const ICON_MAP = {
  BookOpen,
  Clock,
  Gift,
  Layers,
  Lightbulb,
  LogOut,
  PiggyBank,
  Radio,
  Repeat,
  Ruler,
  Scale,
  ShieldOff,
  Target,
  TrendingUp,
  Trophy,
  Umbrella,
  Wallet,
};

const TITLE = "Sports Betting Playbook for Beginners | Parlay Assistant";
const DESCRIPTION =
  "Beginner-friendly lessons on parlays, straight bets, round robins, bankroll management, sportsbook promotions, and betting odds.";

const LessonCard = ({ lesson }) => {
  const IconCmp = ICON_MAP[lesson.icon] || BookOpen;
  return (
    <article
      data-testid={PLAYBOOK.card(lesson.slug)}
      className="pa-card p-5 md:p-6 flex flex-col h-full"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="shrink-0 w-10 h-10 rounded-xl inline-flex items-center justify-center"
          style={{
            background: "rgba(16,185,129,0.14)",
            color: "#059669",
            border: "1px solid rgba(16,185,129,0.3)",
          }}
        >
          <IconCmp size={18} />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-lg md:text-xl uppercase tracking-tight text-slate-900 leading-tight">
            {lesson.title}
          </h3>
          <p className="text-xs text-slate-500 mt-1 font-mono-num">
            {lesson.readingTime} read
          </p>
        </div>
      </div>
      <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-1">
        {lesson.summary}
      </p>
      <Link
        to={`/playbook/${lesson.slug}`}
        data-testid={PLAYBOOK.cardCta(lesson.slug)}
        className="pa-btn pa-btn-primary !text-sm !px-4 mt-5 self-start"
      >
        Read Lesson
      </Link>
    </article>
  );
};

export const PlaybookIndex = () => {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/playbook" });

  const [query, setQuery] = useState("");
  const [completed, setCompleted] = useState(getCompletedSlugs());
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    track(EVENTS.PLAYBOOK_OPENED);
    // Reload completed on mount in case another tab updated it.
    setCompleted(getCompletedSlugs());
  }, []);

  const searchTimer = useRef(null);
  const onSearchChange = (v) => {
    setQuery(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.trim().length >= 2) {
      searchTimer.current = setTimeout(() => {
        // Only track that a search happened + query length — never the text
        // itself, which could contain PII.
        track(EVENTS.PLAYBOOK_SEARCH_USED, { query_length: v.trim().length });
      }, 800);
    }
  };

  const filteredByCategory = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATEGORIES.map((cat) => {
      const lessons = lessonsByCategory(cat.id).filter((l) => {
        if (!q) return true;
        return (
          l.title.toLowerCase().includes(q) ||
          l.summary.toLowerCase().includes(q)
        );
      });
      return { ...cat, lessons };
    }).filter((c) => c.lessons.length > 0);
  }, [query]);

  const total = LESSONS.length;
  const done = completed.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const onReset = () => {
    resetProgress();
    setCompleted([]);
    setShowResetConfirm(false);
  };

  return (
    <main
      data-testid={PLAYBOOK.index}
      className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <div className="max-w-3xl">
        <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
          Learn the Basics
        </p>
        <h1 className="mt-2 font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1.02]">
          The Parlay Assistant Playbook
        </h1>
        <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed">
          Sports betting can seem confusing when you are just getting started. The Parlay
          Assistant Playbook explains common bets, sportsbook promotions, bankroll management,
          odds, and betting tools using simple words and easy examples. Learn what each term
          means before deciding whether a bet is right for you.
        </p>
      </div>

      {/* Progress + search */}
      <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="pa-card-dark p-5 md:col-span-1">
          <div className="flex items-center gap-3">
            <Trophy size={18} className="text-emerald-400" />
            <p className="font-display text-white text-sm uppercase tracking-wide">
              Your Progress
            </p>
          </div>
          <div className="mt-3">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-white text-3xl font-mono-num">
                {done}
              </span>
              <span className="text-slate-400 text-sm">/ {total} lessons ({pct}%)</span>
            </div>
            <div
              data-testid={PLAYBOOK.progressBar}
              className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Playbook progress"
            >
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{
                  width: `${pct}%`,
                  background: "linear-gradient(90deg, #10b981, #34d399)",
                }}
              />
            </div>
            {showResetConfirm ? (
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-xs text-slate-300">Reset all lesson progress?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onReset}
                    data-testid={PLAYBOOK.resetProgress + "-confirm"}
                    className="pa-btn pa-btn-primary !text-xs !py-2"
                  >
                    Yes, reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(false)}
                    className="pa-btn pa-btn-dark-ghost !text-xs !py-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowResetConfirm(true)}
                data-testid={PLAYBOOK.resetProgress}
                className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
              >
                <RotateCcw size={12} /> Reset progress
              </button>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label htmlFor="playbook-search" className="sr-only">
            Search lessons
          </label>
          <div className="relative">
            <Search
              size={18}
              aria-hidden="true"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              id="playbook-search"
              data-testid={PLAYBOOK.search}
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder="Search lessons…"
              value={query}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pa-input font-mono-num text-base"
              style={{ paddingLeft: "2.75rem" }}
            />
          </div>
        </div>
      </div>

      {/* Lessons by category */}
      <div className="mt-10 md:mt-14 space-y-10 md:space-y-14">
        {filteredByCategory.length === 0 && (
          <p className="text-slate-400 text-sm">No lessons match your search.</p>
        )}
        {filteredByCategory.map((cat) => (
          <section key={cat.id} aria-labelledby={`cat-${cat.id}`}>
            <div className="max-w-2xl">
              <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
                {cat.title}
              </p>
              <h2
                id={`cat-${cat.id}`}
                className="mt-1 font-display text-white text-2xl md:text-3xl uppercase tracking-tight"
              >
                {cat.title}
              </h2>
              <p className="mt-2 text-slate-400 text-sm md:text-base">{cat.desc}</p>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {cat.lessons.map((l) => (
                <LessonCard key={l.slug} lesson={l} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default PlaybookIndex;
